import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  type CustomerFieldBindingTransform,
  type CustomerFieldMappingContract,
  type CustomerFieldMappingGroup,
  type CustomerFieldMappingTarget,
  customerFieldTargetOptions,
} from '@/contracts/customers'
import type { CustomerApplication } from '@/lib/business-workflow'
import {
  buildCustomerFieldMappingFromQuestion,
  customerFieldGroupLabels,
  customerFieldTransformLabels,
  getCustomerFieldMappingConflictAdvice,
  getCustomerFieldMappingConflictGroups,
  getCustomerFieldTargetLabel,
  getCustomerFieldTargetOption,
  getMappingConflictCount,
  getQuestionSpecNumber,
  summarizeQuestionOptionRules,
  summarizeQuestionValidationRules,
} from '@/lib/customer-field-mapping-ui'
import { cn } from '@/lib/utils'
import type { Question } from '@/types/form'
import { isLayoutQuestionType } from '@/types/form'
import { Save, TriangleAlert } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

type CustomerFieldMappingPanelProps = {
  formId: string
  formTitle: string
  questions: Question[]
  mappings: CustomerFieldMappingContract[]
  sampleApplication?: CustomerApplication | null
  canEdit: boolean
  onSave: (formId: string, mappings: CustomerFieldMappingContract[]) => void
}

const noBindingValue = '__none__'

export function CustomerFieldMappingPanel({
  formId,
  formTitle,
  questions,
  mappings,
  sampleApplication,
  canEdit,
  onSave,
}: CustomerFieldMappingPanelProps) {
  const [draft, setDraft] = useState<CustomerFieldMappingContract[]>(() =>
    enrichMappings(mappings, questions),
  )

  useEffect(() => {
    setDraft(enrichMappings(mappings, questions))
  }, [mappings, questions])

  const editableQuestions = useMemo(
    () =>
      [...questions]
        .filter((question) => !isLayoutQuestionType(question.type))
        .sort((left, right) => {
          const leftNo = getQuestionSpecNumber(left) ?? left.orderIndex + 1
          const rightNo = getQuestionSpecNumber(right) ?? right.orderIndex + 1
          return leftNo - rightNo
        }),
    [questions],
  )
  const questionById = useMemo(
    () => new Map(questions.map((question) => [question.id, question])),
    [questions],
  )
  const mappedTargets = useMemo(() => new Set(draft.map((mapping) => mapping.targetField)), [draft])
  const fieldRows = useMemo(
    () =>
      customerFieldTargetOptions.filter(
        (option) => option.dbFieldName || mappedTargets.has(option.value),
      ),
    [mappedTargets],
  )
  const conflictGroups = useMemo(() => getCustomerFieldMappingConflictGroups(draft), [draft])
  const mappedCount = draft.filter((mapping) => mapping.formId === formId).length
  const readonlyReason = '只讀檢視：只有管理員可以調整或儲存欄位對應。'

  function replaceTargetBinding(targetField: CustomerFieldMappingTarget, questionId: string) {
    if (!canEdit) return
    setDraft((current) => {
      const withoutTarget = current.filter((mapping) => mapping.targetField !== targetField)
      if (questionId === noBindingValue) return withoutTarget

      const question = editableQuestions.find((item) => item.id === questionId)
      if (!question) return current

      const previous =
        current.find((mapping) => mapping.targetField === targetField) ??
        current.find((mapping) => mapping.questionId === question.id)
      const withoutQuestionAndTarget = current.filter(
        (mapping) => mapping.targetField !== targetField && mapping.questionId !== question.id,
      )
      return [
        ...withoutQuestionAndTarget,
        buildCustomerFieldMappingFromQuestion({
          formId,
          question,
          targetField,
          previous,
        }),
      ]
    })
  }

  function updateMapping(id: string, patch: Partial<CustomerFieldMappingContract>) {
    if (!canEdit) return
    setDraft((current) =>
      current.map((mapping) => {
        if (mapping.id !== id) return mapping
        return enrichMapping(
          {
            ...mapping,
            ...patch,
            updatedAt: new Date().toISOString(),
          },
          questions,
        )
      }),
    )
  }

  function save() {
    onSave(formId, enrichMappings(draft, questions))
  }

  return (
    <section className="rounded-lg border border-indigo-100 bg-indigo-50/30 p-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-slate-950">ERP 欄位綁定清單</h2>
            <Badge variant="outline">mock-safe</Badge>
          </div>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
            依據苗林客戶建檔表單規格，以表格查看每個建議 DB 欄位目前綁定哪一道問卷題目。 這裡只更新
            frontend persistent mock store，不呼叫 D1、Supabase、Email 或外部 ERP。
          </p>
          <div className="mt-2 text-xs text-slate-500">對應問卷：{formTitle}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-600">
            已綁定 {mappedCount} / {fieldRows.length} 個欄位
          </div>
          <Button
            onClick={save}
            disabled={!canEdit}
            title={!canEdit ? readonlyReason : '儲存設定'}
            aria-describedby={!canEdit ? 'customer-field-mapping-readonly-note' : undefined}
          >
            <Save className="size-4" />
            儲存設定
          </Button>
        </div>
      </div>

      {!canEdit ? (
        <div
          id="customer-field-mapping-readonly-note"
          className="mt-4 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600"
        >
          {readonlyReason}
        </div>
      ) : null}

      {conflictGroups.length > 0 ? (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-950">
            <TriangleAlert className="size-4" />
            欄位對應衝突需要處理
          </div>
          <div className="mt-2 grid gap-2">
            {conflictGroups.map((group) => (
              <div key={group.targetField} className="rounded-md bg-white px-3 py-2 text-xs">
                <div className="font-medium text-amber-950">
                  {getCustomerFieldTargetLabel(group.targetField)} 有 {group.items.length}{' '}
                  個題目同時帶入
                </div>
                <p className="mt-1 leading-5 text-amber-800">
                  {getCustomerFieldMappingConflictAdvice(group.targetField, group.items)}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {(Object.keys(customerFieldGroupLabels) as CustomerFieldMappingGroup[]).map((group) => {
          const groupTargets = customerFieldTargetOptions
            .filter(
              (option) =>
                option.group === group && (option.dbFieldName || mappedTargets.has(option.value)),
            )
            .map((option) => option.value)
          if (groupTargets.length === 0) return null
          const groupMappedCount = draft.filter((mapping) =>
            groupTargets.includes(mapping.targetField),
          ).length
          return (
            <div
              key={group}
              className={cn(
                'rounded-md border px-3 py-2',
                groupMappedCount > 0
                  ? 'border-emerald-100 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-white text-slate-500',
              )}
            >
              <div className="text-xs">{customerFieldGroupLabels[group]}</div>
              <div className="mt-1 text-sm font-semibold">
                {groupMappedCount} / {groupTargets.length} 個欄位已對應
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table
          aria-label="ERP 客戶欄位綁定清單"
          className="min-w-[1180px] table-fixed text-left text-sm"
        >
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr>
              <th scope="col" className="w-[220px] px-3 py-3 font-medium">
                ERP 客戶欄位
              </th>
              <th scope="col" className="w-[190px] px-3 py-3 font-medium">
                建議 DB 欄位名
              </th>
              <th scope="col" className="w-[280px] px-3 py-3 font-medium">
                目前綁定題目
              </th>
              <th scope="col" className="w-[320px] px-3 py-3 font-medium">
                選項 / 驗證規則
              </th>
              <th scope="col" className="w-[150px] px-3 py-3 font-medium">
                轉換
              </th>
              <th scope="col" className="w-[120px] px-3 py-3 font-medium">
                狀態
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {fieldRows.map((option) => {
              const targetMappings = draft.filter((mapping) => mapping.targetField === option.value)
              const mapping = targetMappings[0]
              const question = mapping ? questionById.get(mapping.questionId) : undefined
              const hasConflict = getMappingConflictCount(option.value, draft) > 1
              const rowKey = option.dbFieldName ?? option.value
              return (
                <tr
                  key={option.value}
                  data-testid={`field-binding-row-${rowKey}`}
                  className={cn(hasConflict && 'bg-amber-50/40')}
                >
                  <td className="align-top px-3 py-3">
                    <div className="font-medium text-slate-950">{option.shortLabel}</div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">{option.label}</div>
                  </td>
                  <td className="align-top px-3 py-3">
                    <div className="font-mono text-xs text-slate-950">
                      {option.dbFieldName ?? mapping?.dbFieldName ?? '未指定'}
                    </div>
                    <div className="mt-1 font-mono text-xs text-slate-500">
                      {option.dataType ?? mapping?.dataType ?? '未指定'}
                    </div>
                  </td>
                  <td className="align-top px-3 py-3">
                    <select
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 shadow-sm disabled:bg-slate-50 disabled:text-slate-500"
                      value={mapping?.questionId ?? noBindingValue}
                      disabled={!canEdit}
                      aria-label={`${option.shortLabel} 綁定題目`}
                      onChange={(event) =>
                        replaceTargetBinding(option.value, event.currentTarget.value)
                      }
                    >
                      <option value={noBindingValue}>不指定</option>
                      {editableQuestions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {formatQuestionLabel(item)}
                        </option>
                      ))}
                    </select>
                    <div className="mt-2 grid gap-1 text-xs leading-5 text-slate-500">
                      {targetMappings.length > 0 ? (
                        targetMappings.map((item) => (
                          <div key={item.id}>{formatMappingQuestionLabel(item, questionById)}</div>
                        ))
                      ) : (
                        <div>目前沒有題目設定此欄位綁定。</div>
                      )}
                    </div>
                  </td>
                  <td className="align-top px-3 py-3 text-xs leading-5 text-slate-600">
                    <div>
                      {mapping?.validationRuleSummary ??
                        (question ? summarizeQuestionValidationRules(question) : undefined) ??
                        '未設定驗證規則'}
                    </div>
                    <div className="mt-1 text-slate-500">
                      {mapping?.choiceRuleSummary ??
                        (question ? summarizeQuestionOptionRules(question) : undefined) ??
                        '無選項'}
                    </div>
                    <div className="mt-2 text-slate-500">
                      預覽值：{mapping ? getPreviewValue(sampleApplication, mapping) : '未綁定'}
                    </div>
                  </td>
                  <td className="align-top px-3 py-3">
                    {mapping ? (
                      <select
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 shadow-sm disabled:bg-slate-50 disabled:text-slate-500"
                        value={mapping.transform ?? 'trim'}
                        disabled={!canEdit}
                        aria-label={`${option.shortLabel} 資料轉換`}
                        onChange={(event) =>
                          updateMapping(mapping.id, {
                            transform: event.currentTarget.value as CustomerFieldBindingTransform,
                          })
                        }
                      >
                        {(
                          Object.keys(
                            customerFieldTransformLabels,
                          ) as CustomerFieldBindingTransform[]
                        ).map((transform) => (
                          <option key={transform} value={transform}>
                            {customerFieldTransformLabels[transform]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-slate-400">未綁定</span>
                    )}
                  </td>
                  <td className="align-top px-3 py-3">
                    <div className="grid gap-2">
                      {hasConflict ? (
                        <Badge variant="warning" className="w-fit gap-1">
                          <TriangleAlert className="size-3.5" />
                          重複對應
                        </Badge>
                      ) : mapping ? (
                        <Badge variant="outline" className="w-fit">
                          已綁定
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="w-fit">
                          未綁定
                        </Badge>
                      )}
                      {mapping ? (
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={mapping.requiredForApproval}
                            disabled={!canEdit}
                            onChange={(event) =>
                              updateMapping(mapping.id, {
                                requiredForApproval: event.currentTarget.checked,
                              })
                            }
                          />
                          審核必填
                        </label>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function enrichMappings(mappings: CustomerFieldMappingContract[], questions: Question[]) {
  return mappings.map((mapping) => enrichMapping(mapping, questions))
}

function enrichMapping(mapping: CustomerFieldMappingContract, questions: Question[]) {
  const question = questions.find((item) => item.id === mapping.questionId)
  const option = getCustomerFieldTargetOption(mapping.targetField)
  return {
    ...mapping,
    targetLabel: mapping.targetLabel ?? option?.shortLabel ?? option?.label,
    dbFieldName: mapping.dbFieldName ?? option?.dbFieldName,
    dataType: mapping.dataType ?? option?.dataType,
    specQuestionNo:
      mapping.specQuestionNo ?? (question ? getQuestionSpecNumber(question) : undefined),
    choiceRuleSummary:
      mapping.choiceRuleSummary ?? (question ? summarizeQuestionOptionRules(question) : undefined),
    validationRuleSummary:
      mapping.validationRuleSummary ??
      (question ? summarizeQuestionValidationRules(question) : undefined),
  }
}

function formatQuestionLabel(question: Question) {
  const no = getQuestionSpecNumber(question)
  return `${no ? `Q${no} ` : ''}${question.title}`
}

function formatMappingQuestionLabel(
  mapping: CustomerFieldMappingContract,
  questionById: Map<string, Question>,
) {
  const question = questionById.get(mapping.questionId)
  if (question) return formatQuestionLabel(question)
  return `${mapping.specQuestionNo ? `Q${mapping.specQuestionNo} ` : ''}${mapping.questionTitle}`
}

function getPreviewValue(
  application: CustomerApplication | null | undefined,
  mapping: CustomerFieldMappingContract,
) {
  if (!application) return '尚無可預覽的申請資料'
  const matchedAnswer = application.answers.find(
    (answer) =>
      answer.questionTitle === mapping.questionTitle || answer.mappedField === mapping.targetField,
  )
  if (matchedAnswer) return matchedAnswer.answer
  const directValue = (application as unknown as Record<string, string | number | undefined>)[
    mapping.targetField
  ]
  return directValue ? String(directValue) : '此 synthetic 申請未提供對應值'
}
